import dayjs from "dayjs";
import { MONTHS, PROPERTY_TAX_PADDING_MONTHS } from "./constants";
import AppRoutes from "../config/AppRoutes";

const initialValue = 0;
export const getSumFromAccordionItemsArray = (arr) =>
  arr.reduce((accumulator, item) => {
    return Number(accumulator) + Number(item.amount ?? 0);
  }, initialValue);

export const calculatePrePaidMortgageInterest = (
  rate,
  loanAmount,
  plannedClosingDate
) => {
  const today = dayjs(plannedClosingDate);
  const daysInThisMonth = today.daysInMonth();
  const dateOfMonth = today.date();
  const daysOfInterest = daysInThisMonth - (dateOfMonth - 1); // need to be inclusive of current date,
  // so if today is the 31st of May, it should still be 1 day of interest.

  return (rate / 100 / 365) * loanAmount * daysOfInterest;
};

const getFirstPaymentDateFromClosingDate = (closingDate) => {
  const twoMonthsFromClosingDate = closingDate.add(2, "month");
  return twoMonthsFromClosingDate.set("date", 1);
};

const doCalculateHomeOwnersInsuranceEscrow = (
  insurance,
  insurancePaymentMonth,
  plannedClosingDate
) => {
  if (!insurancePaymentMonth || !insurance) {
    return 0;
  }
  const indexOfMonthOfInsurancePayment = MONTHS.indexOf(insurancePaymentMonth);
  const closingDate = dayjs(plannedClosingDate);
  const firstPaymentDate = getFirstPaymentDateFromClosingDate(closingDate);
  const monthDiff = firstPaymentDate.month() - indexOfMonthOfInsurancePayment;
  let monthsOfInsurance = 0;
  if (monthDiff < 0) {
    monthsOfInsurance = 14 - monthDiff;
  } else {
    monthsOfInsurance = 14 - (12 - monthDiff);
  }
  return Math.round((monthsOfInsurance / 12) * insurance);
};

export const calculateHomeOwnersInsuranceEscrow = (
  hasWaivedEscrow,
  insurance,
  insurancePaymentMonth,
  plannedClosingDate,
  lookingTo
) => {
  if (hasWaivedEscrow === "Yes") {
    return 0;
  }

  if (lookingTo === "Build" || lookingTo === "Purchase") {
    return Math.round(Number(insurance ?? 0) / 4);
  }
  return doCalculateHomeOwnersInsuranceEscrow(
    insurance,
    insurancePaymentMonth,
    plannedClosingDate
  );
};

//collect two billing cycles if first payment date month is same as next taxes date month.
export const getBillingCycles = (firstPaymentDate, nextTaxesDate) => {
  return firstPaymentDate.month() === nextTaxesDate.month() &&
    firstPaymentDate.year() === nextTaxesDate.year()
    ? 2
    : 1;
};

export const getMonthsPerCycle = (taxSchedule) => {
  const taxScheduleDayJs = getTaxScheduleDayJs(taxSchedule);
  if (taxScheduleDayJs.length === 4) {
    return 3;
  }
  if (taxScheduleDayJs.length === 2) {
    return 6;
  }
  return 12;
};

//months cannot be the same here
export const getMonthsUntilNextBill = (firstPaymentDate, nextTaxesDate) => {
  const monthsDiff = nextTaxesDate.diff(firstPaymentDate, "month");
  return Math.floor(monthsDiff);
};

export const getFirstPaymentDate = (plannedClosingDate) => {
  const plannedClosingDayJs = dayjs(plannedClosingDate);
  const twoMonthsFromClosing = plannedClosingDayJs.add(2, "month");
  return twoMonthsFromClosing.date(1);
};

export const getTaxScheduleDayJs = (taxSchedule) =>
  taxSchedule
    .map((scheduleItem) => {
      if (scheduleItem === "0000") {
        return null;
      }
      const monthString = scheduleItem.substring(0, 2);
      const dateString = scheduleItem.substring(2);
      const monthNumber = Number(monthString);
      const dateNumber = Number(dateString);

      return dayjs()
        .month(monthNumber - 1)
        .date(dateNumber);
    })
    .filter((item) => item);

export const getNextTaxes = (taxSchedule) => {
  const taxScheduleDayJs = getTaxScheduleDayJs(taxSchedule);

  const testIfFutureDate = (item) => item && item.isAfter(dayjs());

  const index = taxScheduleDayJs.findIndex(testIfFutureDate);
  const indexFound = index !== -1;

  if (indexFound) {
    return taxScheduleDayJs[index];
  }

  //if no items are next this year, it must be the first item next year.
  return taxScheduleDayJs[0].add(1, "year");
};

export const getMonthsOfPropertyTaxes = (
  taxScheduleObject,
  plannedClosingDate
) => {
  const taxSchedule = [
    taxScheduleObject.due1,
    taxScheduleObject.due2,
    taxScheduleObject.due3,
    taxScheduleObject.due4,
  ];
  const firstPaymentDate = getFirstPaymentDate(plannedClosingDate);
  const nextTaxesDate = getNextTaxes(taxSchedule);
  const billingCycles = getBillingCycles(firstPaymentDate, nextTaxesDate);
  const monthsPerCycle = getMonthsPerCycle(taxSchedule);
  const monthsUntilNextBill = getMonthsUntilNextBill(
    firstPaymentDate,
    nextTaxesDate
  );

  return (
    PROPERTY_TAX_PADDING_MONTHS +
    billingCycles * monthsPerCycle -
    monthsUntilNextBill
  );
};

export const getSumOfCredits = (earnestMoney = 0, credits = 0, lookingTo) => {
  if (lookingTo === "Purchase" || lookingTo === "Build") {
    return Number(earnestMoney) + Number(credits);
  }
  return 0;
};

export const calculatePropertyTaxesEscrow = ({
  hasWaivedEscrow,
  taxes,
  state,
  taxSchedule,
  plannedClosingDate,
  lookingTo,
}) => {
  if (hasWaivedEscrow === "Yes") {
    return 0;
  }
  if (!taxes) {
    return 0;
  }
  const isStateMarylandOrConnecticut = state === "MD" || state === "CT";

  const isRefinance =
    lookingTo === "Refinance" || lookingTo === "RefinanceAndCashOut";

  let monthsOfPropertyTaxes;
  if (!isRefinance && !isStateMarylandOrConnecticut) {
    monthsOfPropertyTaxes = 3;
  } else {
    monthsOfPropertyTaxes = getMonthsOfPropertyTaxes(
      taxSchedule,
      plannedClosingDate
    );
  }

  return Math.round((monthsOfPropertyTaxes / 12) * taxes);
};
